import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Skeleton, useTheme } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface ReferrerData {
  referrer_domain: string;
  sessions: number;
  visitors: number;
}

interface ReferrersChartProps {
  data: ReferrerData[];
  loading: boolean;
}

const ReferrersChart: React.FC<ReferrersChartProps> = ({ data, loading }) => {
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Use double rAF to ensure DOM is fully laid out before rendering chart
    let raf2: number;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setMounted(true);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  const chartData = data.slice(0, 10).map((item) => ({
    name: item.referrer_domain || 'Direct',
    sessions: item.sessions,
  }));

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Traffic Sources
        </Typography>

        {loading || !mounted ? (
          <Box sx={{ height: 300 }}>
            <Skeleton variant="rectangular" height="100%" />
          </Box>
        ) : data.length === 0 ? (
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">No referrer data available</Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  type="number"
                  stroke={theme.palette.text.secondary}
                  fontSize={12}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke={theme.palette.text.secondary}
                  fontSize={12}
                  width={80}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 4,
                  }}
                  formatter={(value: number) => [value, 'Sessions']}
                />
                <Bar
                  dataKey="sessions"
                  fill={theme.palette.primary.main}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferrersChart;
